import { parseDateSafe, toWallClockDateTime, formatDateForBackend } from '../utils/utils';

/** Mantine DateInput / dayjs display format, e.g. Wed 01/Jul/26 */
export const GANTT_DATE_DISPLAY_FORMAT = 'ddd DD/MMM/YY';

/** Editable grid format for typed date entry */
export const GANTT_DATE_EDIT_FORMAT_PLACEHOLDER = 'DD/MM/YYYY';

function toGanttDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value == null || String(value).trim() === '') return null;
  const parsed = parseDateSafe(String(value));
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

/** Format a date for Gantt grid labels and read-only displays. */
export function formatGanttDateLabel(value: unknown): string {
  const date = toGanttDate(value);
  if (!date) return '';
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = String(date.getFullYear() % 100).padStart(2, '0');
  return `${weekday} ${day}/${month}/${year}`;
}

export function formatGanttDateFromStored(value: string | null | undefined): string {
  return formatGanttDateLabel(value);
}

/** YYYY-MM-DD from a Date or wall-clock string (local calendar day). */
export function toCalendarDayString(value: string | Date | null | undefined): string | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const wall = toWallClockDateTime(value == null ? null : String(value));
  return wall ? wall.slice(0, 10) : null;
}

export function sameCalendarDay(
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): boolean {
  const da = toCalendarDayString(a);
  const db = toCalendarDayString(b);
  return !!da && !!db && da === db;
}

/**
 * Apply a new calendar day while keeping the existing wall-clock time-of-day.
 * Prevents HTML5 / date-only editors from dirtying Save when they rewrite 08:00 → 00:00.
 */
export function applyCalendarDayKeepingTime(
  daySource: string | Date,
  existing: string | null | undefined,
): string {
  const day = toCalendarDayString(daySource) ?? formatDateForBackend(daySource).slice(0, 10);
  const time = toWallClockDateTime(existing)?.slice(11) ?? '08:00:00';
  return `${day} ${time}`;
}

/** Format for typed inline edit: DD/MM/YYYY */
export function formatDateForInlineEdit(value: unknown): string {
  const date = toGanttDate(value);
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

/** Parse DD/MM/YYYY, YYYY-MM-DD, or other parseDateSafe inputs into a local Date at noon (stable day). */
export function parseDateForInlineEdit(text: string): Date | null {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  const dmy = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const date = new Date(year, month - 1, day, 12, 0, 0, 0);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return date;
      }
    }
    return null;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    const date = new Date(year, month - 1, day, 12, 0, 0, 0);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
    return null;
  }

  return toGanttDate(raw);
}
